import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Activity,
  Cpu,
  HardDrive,
  MemoryStick,
  RefreshCw,
  Shield,
  Clock,
  Zap,
  AlertTriangle,
  CheckCircle,
  Server,
  Gauge,
  ArrowUp,
  ArrowDown,
  Minus,
  Wifi,
  FileText,
  Settings,
  Bell,
  BellOff,
  Save,
  Trash2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// OOM risk badge styling
const riskStyles = {
  low: { color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", label: "LOW" },
  moderate: { color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30", label: "MODERATE" },
  high: { color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/30", label: "HIGH" },
  critical: { color: "text-red-400", bg: "bg-red-500/10 border-red-500/30", label: "CRITICAL" },
};

const MetricCard = ({ icon: Icon, label, value, sub, color = "text-blue-400", trend, testId }) => (
  <Card data-testid={testId} className="bg-slate-900/60 border-slate-700/50 hover:border-slate-600/60 transition-colors">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {sub && <p className="text-xs text-slate-500">{sub}</p>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className={`p-2 rounded-lg bg-slate-800/80 ${color}`}>
            <Icon className="w-4 h-4" />
          </div>
          {trend && (
            <span className={`text-[10px] flex items-center gap-0.5 ${
              trend === "up" ? "text-red-400" : trend === "down" ? "text-emerald-400" : "text-slate-500"
            }`}>
              {trend === "up" ? <ArrowUp className="w-2.5 h-2.5" /> :
               trend === "down" ? <ArrowDown className="w-2.5 h-2.5" /> :
               <Minus className="w-2.5 h-2.5" />}
            </span>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

const ProgressBar = ({ value, max = 100, color = "bg-blue-500", label }) => {
  const pct = Math.min((value / max) * 100, 100);
  const barColor = pct > 90 ? "bg-red-500" : pct > 75 ? "bg-orange-500" : pct > 60 ? "bg-amber-500" : color;
  return (
    <div className="space-y-1">
      {label && (
        <div className="flex justify-between text-xs">
          <span className="text-slate-400">{label}</span>
          <span className="text-slate-300 font-mono">{value.toFixed(1)}%</span>
        </div>
      )}
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// Mini sparkline using simple divs (no chart library needed)
const Sparkline = ({ data, height = 40 }) => {
  if (!data || data.length < 2) return null;
  const values = data.map((d) => d.rss_mb);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  return (
    <div data-testid="memory-sparkline" className="flex items-end gap-px" style={{ height }}>
      {values.slice(-60).map((v, i) => {
        const h = ((v - min) / range) * height;
        const pct = ((v - min) / range) * 100;
        const color = pct > 80 ? "bg-red-500" : pct > 50 ? "bg-amber-500" : "bg-emerald-500";
        return <div key={i} className={`flex-1 rounded-t-sm min-w-[2px] ${color} opacity-80`} style={{ height: Math.max(h, 2) }} />;
      })}
    </div>
  );
};

export default function HealthDashboardPage() {
  usePageTitle("System Health");
  const { token } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [sortField, setSortField] = useState("requests");
  const [sortDir, setSortDir] = useState("desc");
  const prevRss = useRef(null);
  const [rssTrend, setRssTrend] = useState(null);

  // Alert state
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [thresholds, setThresholds] = useState({ ram_pct: 80, rss_mb: 500 });
  const [editThresholds, setEditThresholds] = useState({ ram_pct: 80, rss_mb: 500 });
  const [savingThresholds, setSavingThresholds] = useState(false);
  const lastAlertTime = useRef({});

  const fireAlerts = useCallback((alerts) => {
    if (!alertsEnabled || !alerts?.length) return;
    const now = Date.now();
    alerts.forEach((a) => {
      // Throttle: same alert type only once per 60s
      if (lastAlertTime.current[a.type] && now - lastAlertTime.current[a.type] < 60000) return;
      lastAlertTime.current[a.type] = now;
      const toastFn = a.severity === "critical" ? toast.error : toast.warning;
      toastFn(a.message, { duration: 8000, id: `health-alert-${a.type}` });
    });
  }, [alertsEnabled]);

  const fetchAll = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [metricsRes, endpointsRes, historyRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/health/metrics`, { headers }),
        fetch(`${BACKEND_URL}/api/health/endpoints`, { headers }),
        fetch(`${BACKEND_URL}/api/health/history`, { headers }),
      ]);

      if (metricsRes.ok) {
        const m = await metricsRes.json();
        setMetrics(m);
        if (m.thresholds) {
          setThresholds(m.thresholds);
          setEditThresholds(m.thresholds);
        }
        // Fire toast alerts
        fireAlerts(m.alerts);
        // Track RSS trend
        if (prevRss.current !== null) {
          if (m.process.rss_mb > prevRss.current + 5) setRssTrend("up");
          else if (m.process.rss_mb < prevRss.current - 5) setRssTrend("down");
          else setRssTrend("stable");
        }
        prevRss.current = m.process.rss_mb;
      }
      if (endpointsRes.ok) {
        const e = await endpointsRes.json();
        setEndpoints(e.endpoints || []);
      }
      if (historyRes.ok) {
        const h = await historyRes.json();
        setHistory(h.history || []);
      }
      setLastUpdate(new Date());
    } catch (err) {
      toast.error("Failed to fetch health metrics");
    } finally {
      setLoading(false);
    }
  }, [token, fireAlerts]);

  const saveThresholds = async () => {
    setSavingThresholds(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/health/thresholds`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(editThresholds),
      });
      if (res.ok) {
        const saved = await res.json();
        setThresholds(saved);
        setEditThresholds(saved);
        toast.success("Alert thresholds updated");
        setShowSettings(false);
      } else {
        toast.error("Failed to save thresholds");
      }
    } catch {
      toast.error("Failed to save thresholds");
    } finally {
      setSavingThresholds(false);
    }
  };

  const resetThresholdsToAuto = async () => {
    setSavingThresholds(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/health/thresholds/reset`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setThresholds(data.thresholds);
        setEditThresholds(data.thresholds);
        toast.success(`Reset to recommended (RSS ${data.thresholds.rss_mb} MB for this pod)`);
      } else {
        toast.error("Failed to reset thresholds");
      }
    } catch {
      toast.error("Failed to reset thresholds");
    } finally {
      setSavingThresholds(false);
    }
  };

  const forceGarbageCollection = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/health/gc`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`GC: ${data.before_mb} MB → ${data.after_mb} MB (freed ${data.freed_mb} MB)`);
        fetchAll();
      } else {
        toast.error(data.detail || "GC failed");
      }
    } catch {
      toast.error("GC failed");
    }
  };

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchAll, 15000); // 15s
    return () => clearInterval(interval);
  }, [autoRefresh, fetchAll]);

  const sortedEndpoints = [...endpoints].sort((a, b) => {
    const av = a[sortField], bv = b[sortField];
    return sortDir === "desc" ? (bv - av) : (av - bv);
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortField(field); setSortDir("desc"); }
  };

  const risk = metrics ? riskStyles[metrics.oom_risk] || riskStyles.low : riskStyles.low;

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-cyan-400 animate-pulse" />
          <h1 className="text-2xl font-bold text-white">System Health Monitor</h1>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="bg-slate-900/60 border-slate-700/50">
              <CardContent className="p-4"><div className="h-16 bg-slate-800 rounded animate-pulse" /></CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="health-dashboard" className="p-4 md:p-6 space-y-6 max-w-[1600px]">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-cyan-500/10">
            <Activity className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">System Health Monitor</h1>
            <p className="text-xs text-slate-500">
              {lastUpdate ? `Updated ${lastUpdate.toLocaleTimeString()}` : "Loading..."}
              {metrics && ` · Uptime ${metrics.uptime}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="alerts-toggle"
            variant={alertsEnabled ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setAlertsEnabled(!alertsEnabled);
              toast.info(alertsEnabled ? "Alerts paused" : "Alerts enabled");
            }}
            className={alertsEnabled ? "bg-amber-600 hover:bg-amber-700 text-white" : "border-slate-600 text-slate-300"}
          >
            {alertsEnabled ? <Bell className="w-3.5 h-3.5 mr-1.5" /> : <BellOff className="w-3.5 h-3.5 mr-1.5" />}
            {alertsEnabled ? "Alerts On" : "Alerts Off"}
          </Button>
          <Button
            data-testid="settings-btn"
            variant="outline"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
            className={`border-slate-600 text-slate-300 hover:bg-slate-800 ${showSettings ? "bg-slate-800" : ""}`}
          >
            <Settings className="w-3.5 h-3.5 mr-1.5" />
            Thresholds
          </Button>
          <Button
            data-testid="auto-refresh-toggle"
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-cyan-600 hover:bg-cyan-700 text-white" : "border-slate-600 text-slate-300"}
          >
            <Wifi className={`w-3.5 h-3.5 mr-1.5 ${autoRefresh ? "animate-pulse" : ""}`} />
            {autoRefresh ? "Live" : "Paused"}
          </Button>
          <Button
            data-testid="refresh-btn"
            variant="outline"
            size="sm"
            onClick={fetchAll}
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Alert Threshold Settings Panel */}
      {showSettings && (
        <Card data-testid="threshold-settings" className="bg-slate-900/80 border-amber-600/30 animate-in fade-in slide-in-from-top-2 duration-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Settings className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-semibold text-slate-200">Alert Thresholds</h3>
              <span className="text-xs text-slate-500 ml-2">Toast notifications trigger when these limits are exceeded</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-400 flex items-center gap-1.5">
                  <Server className="w-3 h-3" /> System RAM Usage (%)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    data-testid="threshold-ram-input"
                    type="number"
                    min={10}
                    max={99}
                    step={5}
                    value={editThresholds.ram_pct}
                    onChange={(e) => setEditThresholds({ ...editThresholds, ram_pct: parseFloat(e.target.value) || 80 })}
                    className="w-24 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-sm text-slate-200 font-mono focus:border-amber-500 focus:outline-none"
                  />
                  <span className="text-xs text-slate-500">Current: <strong className={metrics?.system?.used_pct >= editThresholds.ram_pct ? "text-red-400" : "text-emerald-400"}>{metrics?.system?.used_pct ?? "—"}%</strong></span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-400 flex items-center gap-1.5">
                  <MemoryStick className="w-3 h-3" /> Process RSS Memory (MB)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    data-testid="threshold-rss-input"
                    type="number"
                    min={50}
                    max={10000}
                    step={50}
                    value={editThresholds.rss_mb}
                    onChange={(e) => setEditThresholds({ ...editThresholds, rss_mb: parseFloat(e.target.value) || 500 })}
                    className="w-24 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-sm text-slate-200 font-mono focus:border-amber-500 focus:outline-none"
                  />
                  <span className="text-xs text-slate-500">Current: <strong className={metrics?.process?.rss_mb >= editThresholds.rss_mb ? "text-red-400" : "text-emerald-400"}>{metrics?.process?.rss_mb ?? "—"} MB</strong></span>
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-3 gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={resetThresholdsToAuto}
                disabled={savingThresholds}
                className="border-emerald-600/50 text-emerald-300 hover:bg-emerald-900/30"
                data-testid="reset-thresholds-btn"
                title="Auto-scales the RSS threshold to ~60% of this pod's total RAM"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                Reset to Recommended
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={forceGarbageCollection}
                className="border-blue-600/50 text-blue-300 hover:bg-blue-900/30"
                data-testid="force-gc-btn"
                title="Run Python garbage collection now to release memory back to the OS"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Force GC Now
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setEditThresholds(thresholds); setShowSettings(false); }}
                className="border-slate-600 text-slate-400"
              >
                Cancel
              </Button>
              <Button
                data-testid="save-thresholds-btn"
                size="sm"
                onClick={saveThresholds}
                disabled={savingThresholds}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Save className="w-3.5 h-3.5 mr-1.5" />
                {savingThresholds ? "Saving..." : "Save Thresholds"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Alerts Banner */}
      {metrics?.alerts?.length > 0 && alertsEnabled && (
        <div data-testid="active-alerts-banner" className="space-y-2">
          {metrics.alerts.map((alert, idx) => {
            const isCritical = alert.severity === "critical";
            return (
              <div key={idx} className={`p-3 rounded-lg border flex items-center gap-3 ${
                isCritical ? "bg-red-500/10 border-red-500/30" : "bg-amber-500/10 border-amber-500/30"
              }`}>
                <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${isCritical ? "text-red-400" : "text-amber-400"}`} />
                <div className="flex-1">
                  <span className={`font-semibold text-sm ${isCritical ? "text-red-400" : "text-amber-400"}`}>
                    {alert.type === "ram" ? "RAM Alert" : "RSS Alert"}
                  </span>
                  <span className="text-slate-400 text-sm ml-2">{alert.message}</span>
                </div>
                <Badge variant="outline" className={`text-[10px] ${
                  isCritical ? "border-red-800 text-red-400" : "border-amber-800 text-amber-400"
                }`}>
                  {alert.severity.toUpperCase()}
                </Badge>
              </div>
            );
          })}
        </div>
      )}

      {/* OOM Risk Banner */}
      {metrics && (metrics.oom_risk === "high" || metrics.oom_risk === "critical") && (
        <div data-testid="oom-risk-banner" className={`p-3 rounded-lg border flex items-center gap-3 ${risk.bg}`}>
          <AlertTriangle className={`w-5 h-5 ${risk.color} flex-shrink-0`} />
          <div>
            <span className={`font-semibold ${risk.color}`}>OOM Risk: {risk.label}</span>
            <span className="text-slate-400 text-sm ml-2">
              System memory at {metrics.system.used_pct}% — process using {metrics.process.rss_mb} MB.
              Consider restarting or scaling up.
            </span>
          </div>
        </div>
      )}

      {/* Top metrics grid */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            testId="metric-process-memory"
            icon={MemoryStick}
            label="Process Memory"
            value={`${metrics.process.rss_mb} MB`}
            sub={`${metrics.process.proc_mem_pct}% of system`}
            color="text-cyan-400"
            trend={rssTrend}
          />
          <MetricCard
            testId="metric-system-memory"
            icon={Server}
            label="System Memory"
            value={`${metrics.system.used_pct}%`}
            sub={`${metrics.system.available_mb} MB free of ${metrics.system.total_mb} MB`}
            color={metrics.system.used_pct > 80 ? "text-red-400" : metrics.system.used_pct > 60 ? "text-amber-400" : "text-emerald-400"}
          />
          <MetricCard
            testId="metric-cpu"
            icon={Cpu}
            label="CPU Usage"
            value={`${metrics.system.cpu_pct}%`}
            sub={`${metrics.system.cpu_count} cores`}
            color={metrics.system.cpu_pct > 80 ? "text-red-400" : "text-violet-400"}
          />
          <MetricCard
            testId="metric-oom-risk"
            icon={Shield}
            label="OOM Risk"
            value={risk.label}
            sub={`Uptime: ${metrics.uptime}`}
            color={risk.color}
          />
        </div>
      )}

      {/* Second row: disk + process details + memory chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Disk & Process */}
        {metrics && (
          <Card className="bg-slate-900/60 border-slate-700/50 lg:col-span-1">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-slate-400" /> System Details
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <ProgressBar value={metrics.disk.used_pct} label="Disk Usage" color="bg-violet-500" />
              <div className="text-xs text-slate-500">
                {metrics.disk.used_gb} GB / {metrics.disk.total_gb} GB ({metrics.disk.free_gb} GB free)
              </div>
              <ProgressBar value={metrics.system.used_pct} label="RAM Usage" color="bg-cyan-500" />
              <ProgressBar value={metrics.process.proc_mem_pct} max={100} label="Process RAM Share" color="bg-emerald-500" />
              <div className="grid grid-cols-3 gap-2 pt-2">
                <div className="text-center p-2 rounded bg-slate-800/60">
                  <p className="text-lg font-bold text-slate-200">{metrics.process.threads}</p>
                  <p className="text-[10px] text-slate-500">Threads</p>
                </div>
                <div className="text-center p-2 rounded bg-slate-800/60">
                  <p className="text-lg font-bold text-slate-200">{metrics.process.open_files}</p>
                  <p className="text-[10px] text-slate-500">Files</p>
                </div>
                <div className="text-center p-2 rounded bg-slate-800/60">
                  <p className="text-lg font-bold text-slate-200">{metrics.process.connections}</p>
                  <p className="text-[10px] text-slate-500">Connections</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Memory History Sparkline */}
        <Card className="bg-slate-900/60 border-slate-700/50 lg:col-span-2">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-medium text-slate-300 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Gauge className="w-4 h-4 text-slate-400" /> Memory Trend (RSS)
              </span>
              {history.length > 0 && (
                <span className="text-xs font-normal text-slate-500">{history.length} data points</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {history.length > 1 ? (
              <div className="space-y-2">
                <Sparkline data={history} height={80} />
                <div className="flex justify-between text-[10px] text-slate-600">
                  <span>{history[0]?.timestamp ? new Date(history[0].timestamp).toLocaleTimeString() : ""}</span>
                  <span>{history[history.length - 1]?.timestamp ? new Date(history[history.length - 1].timestamp).toLocaleTimeString() : ""}</span>
                </div>
                <div className="flex gap-4 text-xs text-slate-500">
                  <span>Min: <strong className="text-emerald-400">{Math.min(...history.map(h => h.rss_mb)).toFixed(1)} MB</strong></span>
                  <span>Max: <strong className="text-red-400">{Math.max(...history.map(h => h.rss_mb)).toFixed(1)} MB</strong></span>
                  <span>Current: <strong className="text-cyan-400">{history[history.length - 1]?.rss_mb} MB</strong></span>
                </div>
              </div>
            ) : (
              <div className="h-20 flex items-center justify-center text-sm text-slate-600">
                Collecting data... metrics will appear after a few refreshes.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Endpoint Performance Table */}
      <Card className="bg-slate-900/60 border-slate-700/50">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-medium text-slate-300 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" /> Endpoint Performance
            </span>
            <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
              {endpoints.length} endpoints tracked
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0 pb-2">
          <div className="overflow-x-auto">
            <table data-testid="endpoints-table" className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-slate-500 text-xs uppercase">
                  <th className="text-left px-4 py-2 font-medium">Endpoint</th>
                  <th className="text-right px-4 py-2 font-medium cursor-pointer hover:text-slate-300" onClick={() => toggleSort("requests")}>
                    Requests {sortField === "requests" && (sortDir === "desc" ? "↓" : "↑")}
                  </th>
                  <th className="text-right px-4 py-2 font-medium cursor-pointer hover:text-slate-300" onClick={() => toggleSort("avg_ms")}>
                    Avg (ms) {sortField === "avg_ms" && (sortDir === "desc" ? "↓" : "↑")}
                  </th>
                  <th className="text-right px-4 py-2 font-medium cursor-pointer hover:text-slate-300" onClick={() => toggleSort("max_ms")}>
                    Max (ms) {sortField === "max_ms" && (sortDir === "desc" ? "↓" : "↑")}
                  </th>
                  <th className="text-right px-4 py-2 font-medium cursor-pointer hover:text-slate-300" onClick={() => toggleSort("errors")}>
                    Errors {sortField === "errors" && (sortDir === "desc" ? "↓" : "↑")}
                  </th>
                  <th className="text-right px-4 py-2 font-medium">Error %</th>
                </tr>
              </thead>
              <tbody>
                {sortedEndpoints.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-600">
                      No requests tracked yet. Interact with the app to generate data.
                    </td>
                  </tr>
                ) : (
                  sortedEndpoints.map((ep, i) => {
                    const avgColor = ep.avg_ms > 2000 ? "text-red-400" : ep.avg_ms > 500 ? "text-amber-400" : "text-emerald-400";
                    const maxColor = ep.max_ms > 5000 ? "text-red-400" : ep.max_ms > 1000 ? "text-amber-400" : "text-slate-300";
                    return (
                      <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                        <td className="px-4 py-2">
                          <code className="text-xs text-slate-300 font-mono bg-slate-800/60 px-1.5 py-0.5 rounded">
                            {ep.endpoint}
                          </code>
                        </td>
                        <td className="text-right px-4 py-2 font-mono text-slate-300">{ep.requests.toLocaleString()}</td>
                        <td className={`text-right px-4 py-2 font-mono ${avgColor}`}>{ep.avg_ms.toFixed(0)}</td>
                        <td className={`text-right px-4 py-2 font-mono ${maxColor}`}>{ep.max_ms.toFixed(0)}</td>
                        <td className="text-right px-4 py-2 font-mono">
                          {ep.errors > 0 ? (
                            <span className="text-red-400">{ep.errors}</span>
                          ) : (
                            <span className="text-slate-600">0</span>
                          )}
                        </td>
                        <td className="text-right px-4 py-2 font-mono">
                          {ep.error_rate > 0 ? (
                            <Badge variant="outline" className="text-[10px] border-red-800 text-red-400 bg-red-500/10">
                              {ep.error_rate}%
                            </Badge>
                          ) : (
                            <span className="text-slate-600">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
